// Pure decision for which file a texture_assets row should load from
// (ENG-753 variant preference + ENG-757 quality tiers). Kept dependency-free
// (no THREE) so it unit-tests in node and stays the single place the
// variant-vs-original policy lives.

// TIER_EDGE maps a quality tier to the max texture edge it targets. `ultra`
// means the full-resolution variant (ktx2_url). Mirrors the server's ladder
// rungs (texture_transcode.LadderRungs).
export const TIER_EDGE = { low: 512, medium: 1024, high: 2048, ultra: Infinity }

// chooseTextureSource resolves the best loadable file for a texture_assets row
// at a quality tier. Preference order:
//   1. The ktx2_variants ladder rung closest to the tier's edge — the largest
//      done rung at/below it, else the smallest done rung above it (closer to
//      the request than jumping to full resolution).
//   2. The full-resolution KTX2 (ktx2_status === 'done') — also the direct
//      target for tier 'ultra' and for rows without a usable ladder.
//   3. The original PNG/JPG (file_url).
// KTX2 options require ktx2Ready (loader initialised + supported here). When
// there is nothing usable to load it returns null — callers must not fabricate
// a texture (loud-empty, per the server-is-source-of-truth UX rules).
//
//   row       — a texture_assets row: { file_url, ktx2_url, ktx2_status,
//               ktx2_variants?, ... }
//   ktx2Ready — whether the KTX2 loader was initialised + supported here
//   tier      — 'low' | 'medium' | 'high' | 'ultra' (default 'ultra' — existing
//               callers keep ENG-753 behavior)
//
// Returns { url, ktx2: boolean, edge: number } or null. `edge` is the max edge
// of the chosen source (Infinity = full resolution) so the cascade's upgrade
// pass can compare quality levels without re-deriving them.
export function chooseTextureSource(row, ktx2Ready, tier = 'ultra') {
  if (!row) return null
  if (ktx2Ready) {
    const edge = TIER_EDGE[tier] ?? Infinity
    if (edge !== Infinity) {
      const rung = pickRung(row.ktx2_variants, edge)
      if (rung) return { url: rung.url, ktx2: true, edge: rung.edge }
    }
    if (row.ktx2_status === 'done' && row.ktx2_url) {
      return { url: row.ktx2_url, ktx2: true, edge: Infinity }
    }
  }
  if (row.file_url) return { url: row.file_url, ktx2: false, edge: Infinity }
  return null
}

// shouldSwapTexture is the cascade's swap policy (ENG-757): swap a bound
// texture for a newly-resolved source only when it is a genuine change, and
// never a quality downgrade unless explicitly allowed (the user manually
// picking a lower tier). current/next are { url, edge } — edge as returned by
// chooseTextureSource.
export function shouldSwapTexture(current, next, allowDowngrade = false) {
  if (!next || !next.url) return false
  if (!current) return true
  if (next.url === current.url) return false
  if (!allowDowngrade && next.edge <= current.edge) return false
  return true
}

// pickRung selects the ladder rung nearest a target edge: the largest done
// rung at/below it, else the smallest done rung above it. Absent or malformed
// ladders yield null (callers fall through to the ENG-753 preference chain).
function pickRung(variants, targetEdge) {
  if (!variants || typeof variants !== 'object') return null
  const done = []
  for (const [key, entry] of Object.entries(variants)) {
    const edge = Number(key)
    if (!Number.isFinite(edge) || edge <= 0) continue
    if (entry && entry.status === 'done' && entry.url) done.push({ edge, url: entry.url })
  }
  if (!done.length) return null
  done.sort((a, b) => a.edge - b.edge)
  const atOrBelow = done.filter(d => d.edge <= targetEdge)
  if (atOrBelow.length) return atOrBelow[atOrBelow.length - 1]
  return done[0]
}
